import * as codeSystems from '@seen/core/codeSystems'
import { ExtendedObservation } from '@seen/core/models/observation'
import { parseCondition } from '@seen/core/observation-logic/conditionParser'
import { printCondition } from '@seen/core/observation-logic/conditionPrinter'
import type { Condition } from '@seen/core/observation-logic/conditionSchema'
import { selectedRule } from '@seen/core/observation-logic/derivations/rules'
import { STARTER_DERIVATIONS } from '@seen/core/observation-logic/derivations/starter'
import {
  CONDITIONAL_SECTION_NODE,
  CONDITIONAL_SPAN_NODE,
  isSectionLike,
  OBSERVATION_PANEL_NODE,
  readCondition,
} from '@seen/core/observation-logic/documentUnits'
import { bandSetFor } from '@seen/core/observation-logic/interpretation'
import { OBSERVATION_MODE_LATEST } from '@seen/core/observationReferences'
import { codeSystemName } from '@seen/core/terminology/codeSystemNames'
import {
  ASSESSMENT_ANSWERS_SYSTEM,
  ASSESSMENT_QUESTIONS_SYSTEM,
} from '@seen/core/terminology/types'
import { Button, Tabs, TabsContent, TabsList, TabsTrigger } from '@seen/ui'
import { IconLink, IconPencil } from '@tabler/icons-react'
import type { Node as PMNode } from '@tiptap/pm/model'
import type { Editor } from '@tiptap/react'
import { type JSX, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'
import { z } from 'zod'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormLabel } from '#src/components/core/form/FormLabel.tsx'
import { Input } from '#src/components/core/input/Input.tsx'
import { Switch } from '#src/components/core/Switch.tsx'
import { Textarea } from '#src/components/core/Textarea.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { ConditionBuilder } from '#src/components/rich-text-editor/conditional-display/ConditionBuilder.tsx'
import {
  type BuilderState,
  builderToCondition,
  type ConditionSource,
  conditionToBuilder,
  contextSources,
  EMPTY_BUILDER,
  generatedName,
  nameProblem,
  noteNodes,
  noteSources,
  pickedAnswers,
  renameInCondition,
} from '#src/components/rich-text-editor/conditional-display/conditionBuilderModel.ts'
import {
  type DerivedNodeAttributes,
  derivationCutoffs,
  derivationMethods,
} from '#src/components/rich-text-editor/ObservationNode/derived/derivedNode.ts'
import {
  frozenAttrs,
  frozenPanelAttrs,
  resolveUnfrozenAttrs,
  unfrozenPanelAttrs,
} from '#src/components/rich-text-editor/ObservationNode/observationFreeze.ts'
import type { ObservationNodeAttributes } from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useForm } from '#src/hooks/useForm.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'

export interface ConditionSheetProps {
  editor: Editor
  // The observation node or conditional section being configured.
  pos: number
  node: PMNode
  onDone: () => void
}

const schema = z.object({
  name: z.string(),
  label: z.string(),
  required: z.boolean(),
  showLatest: z.boolean(),
  derivedRuleId: z.string(),
  derivedBandsId: z.string(),
})

const SELECT_CLASS =
  'h-9 w-full rounded-md border bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-950'

// Whether the editor edits a template, where questions are marked required.
function editsTemplate(editor: Editor): boolean {
  return !!editor.extensionManager.extensions.find(
    (extension) => extension.name === 'conditionalDisplay'
  )?.options.isTemplate
}

// Where a code is defined: LOINC's page for the code, or our own
// observations settings, which opens narrowed to it.
function codeSource(
  system: string,
  code: string
): { href: string; external: boolean } | null {
  if (system === codeSystems.LOINC_CODE_SYSTEM) {
    return {
      href: `https://loinc.org/${encodeURIComponent(code)}/`,
      external: true,
    }
  }
  if (
    system === ASSESSMENT_QUESTIONS_SYSTEM ||
    system === ASSESSMENT_ANSWERS_SYSTEM
  ) {
    return {
      href: `/settings/observations?code=${encodeURIComponent(code)}`,
      external: false,
    }
  }
  return null
}

// Names an observation node and sets when it (or a section) shows: a
// builder of rows, or the condition as text under Advanced, kept in step.
export function ConditionSheet({
  editor,
  pos,
  node,
  onDone,
}: ConditionSheetProps): JSX.Element {
  const isSection = isSectionLike(node.type.name)
  // Only a plain section can be removed here, its content kept.
  const removable =
    node.type.name === CONDITIONAL_SECTION_NODE ||
    node.type.name === CONDITIONAL_SPAN_NODE
  // A panel has no label of its own to set.
  const isPanel = node.type.name === OBSERVATION_PANEL_NODE
  // A template marks the questions a note must answer before it is signed.
  const canRequire = editsTemplate(editor) && (isPanel || !isSection)
  const named = useMemo(
    () => noteNodes(editor.state.doc, pos, node),
    [editor, pos, node]
  )
  const sources = useMemo<ConditionSource[]>(
    () => [...contextSources(), ...noteSources(named)],
    [named]
  )
  const stored = readCondition(node.attrs.showWhen)
  const [builder, setBuilder] = useState<BuilderState>(
    () => conditionToBuilder(stored, sources) ?? EMPTY_BUILDER
  )
  const [text, setText] = useState(() => (stored ? printCondition(stored) : ''))
  const [tab, setTab] = useState<'builder' | 'advanced'>(() =>
    stored && !conditionToBuilder(stored, sources) ? 'advanced' : 'builder'
  )
  const parsed = text.trim() ? parseCondition(text) : null
  const medplum = useMedplum()
  // Every field is referable by a name: one it was not given is spoken from
  // its label, and kept when the panel is saved.
  const takenNames = useMemo(
    () => named.flatMap((other) => (other.name ? [other.name] : [])),
    [named]
  )
  const title =
    isSection || isPanel
      ? ''
      : ExtendedObservation.getNickname(
          node.attrs.code,
          node.attrs.codingDisplay || node.attrs.display
        )
  const generated = useMemo(
    () => generatedName(title, takenNames),
    [title, takenNames]
  )
  const wasLatest = node.attrs.mode === OBSERVATION_MODE_LATEST
  const canFreeze = isPanel || !isSection
  // A computed field's method, when its code has more than one (BSA by
  // Mosteller or Du Bois), and cutoffs, when it has more than one published
  // set (BMI for Asian American or WHO adults).
  // As the field will be once unfrozen, so a frozen one offers them too.
  const derivedAttrs: DerivedNodeAttributes = {
    ...(node.attrs as DerivedNodeAttributes),
    mode: null,
  }
  const methods = isSection || isPanel ? [] : derivationMethods(derivedAttrs)
  const cutoffs = isSection || isPanel ? [] : derivationCutoffs(derivedAttrs)
  const chosenRuleId =
    selectedRule(
      { system: derivedAttrs.system, code: derivedAttrs.code },
      STARTER_DERIVATIONS,
      derivedAttrs.derivedRuleId || undefined
    )?.id ?? ''
  const chosenBandsId =
    bandSetFor(
      { system: derivedAttrs.system, code: derivedAttrs.code },
      derivedAttrs.derivedBandsId
    )?.id ?? ''
  const [editingName, setEditingName] = useState(false)
  const form = useForm({
    schema,
    defaultValues: {
      name: (node.attrs.name as string | null) || generated,
      label: (node.attrs.label as string | null) ?? '',
      required: !!node.attrs.required,
      showLatest: wasLatest,
      derivedRuleId: chosenRuleId,
      derivedBandsId: chosenBandsId,
    },
  })
  // The strip can freeze, unfreeze or clear Required while the panel is
  // open: switches the user has not touched follow the node, and Save
  // writes only the ones they changed.
  const isRequired = !!node.attrs.required
  useEffect(() => {
    if (!form.getFieldState('showLatest').isDirty) {
      form.resetField('showLatest', { defaultValue: wasLatest })
    }
  }, [form, wasLatest])
  useEffect(() => {
    if (!form.getFieldState('required').isDirty) {
      form.resetField('required', { defaultValue: isRequired })
    }
  }, [form, isRequired])
  const name = form.watch('name')
  const problem = isSection ? null : nameProblem(name, takenNames)

  const condition = (): Condition | null | 'invalid' => {
    if (tab === 'advanced') {
      if (!parsed) {
        return null
      }
      return parsed.ok ? parsed.condition : 'invalid'
    }
    return builderToCondition(builder, sources)
  }

  const save = form.handleSubmit(async (values) => {
    const next = condition()
    if (next === 'invalid' || problem) {
      return
    }
    const previousName = (node.attrs.name as string | null) ?? ''
    const newName = values.name.trim() || generated
    let modeAttrs: Partial<ObservationNodeAttributes> | null = null
    const changedLatest =
      form.getFieldState('showLatest').isDirty &&
      values.showLatest !== wasLatest
    const changedRequired =
      form.getFieldState('required').isDirty && values.required !== isRequired
    const frozenAfter = changedLatest ? values.showLatest : wasLatest
    if (canFreeze && changedLatest) {
      if (isPanel) {
        modeAttrs = values.showLatest
          ? frozenPanelAttrs(node.attrs.panelReference)
          : unfrozenPanelAttrs()
      } else {
        modeAttrs = values.showLatest
          ? frozenAttrs(node.attrs)
          : await resolveUnfrozenAttrs(node.attrs, medplum)
      }
    }
    editor
      .chain()
      .command(({ tr }) => {
        tr.setNodeAttribute(pos, 'showWhen', next)
        // A frozen field is never answered, so never required.
        if (canRequire && changedRequired && !frozenAfter) {
          tr.setNodeAttribute(pos, 'required', values.required)
        }
        for (const [key, value] of Object.entries(modeAttrs ?? {})) {
          tr.setNodeAttribute(pos, key, value)
        }
        // A new method recalculates, dropping a value set by hand.
        if (
          methods.length > 1 &&
          values.derivedRuleId &&
          values.derivedRuleId !== chosenRuleId
        ) {
          tr.setNodeAttribute(pos, 'derivedRuleId', values.derivedRuleId)
          tr.setNodeAttribute(pos, 'derivedOverride', false)
        }
        if (
          cutoffs.length > 1 &&
          values.derivedBandsId &&
          values.derivedBandsId !== chosenBandsId
        ) {
          tr.setNodeAttribute(pos, 'derivedBandsId', values.derivedBandsId)
        }
        if (isPanel) {
          return true
        }
        if (isSection) {
          tr.setNodeAttribute(pos, 'label', values.label.trim() || null)
          return true
        }
        tr.setNodeAttribute(pos, 'name', newName)
        // A rename carries every condition that read the old name along,
        // this one's included.
        if (previousName && previousName !== newName) {
          tr.doc.descendants((other, at) => {
            const otherCondition = readCondition(other.attrs.showWhen)
            if (otherCondition) {
              tr.setNodeAttribute(
                at,
                'showWhen',
                renameInCondition(otherCondition, previousName, newName)
              )
            }
            return true
          })
        }
        return true
      })
      .run()
    onDone()
  })

  const showLatest = form.watch('showLatest')
  const requiredField = canRequire && (
    <FormField
      control={form.control}
      name="required"
      render={({ field }) => {
        const requiredSwitch = (
          <div className="flex items-center gap-2">
            <Switch
              id="node-required"
              checked={field.value}
              disabled={showLatest}
              className={showLatest ? 'pointer-events-none' : undefined}
              onCheckedChange={field.onChange}
            />
            <FormLabel htmlFor="node-required">Required</FormLabel>
          </div>
        )
        return showLatest ? (
          <Tooltip>
            <TooltipTrigger asChild>
              {/* oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- the disabled switch cannot take focus or hover, so this span is what triggers the tooltip */}
              <span tabIndex={0} className="w-fit">
                {requiredSwitch}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              A field showing its latest value isn&apos;t answered, so it
              can&apos;t be required
            </TooltipContent>
          </Tooltip>
        ) : (
          requiredSwitch
        )
      }}
    />
  )

  const showLatestField = canFreeze && (
    <FormField
      control={form.control}
      name="showLatest"
      render={({ field }) => (
        <div className="flex items-start gap-2">
          <Switch
            id="node-show-latest"
            className="mt-0.5"
            checked={field.value}
            onCheckedChange={(checked) => {
              field.onChange(checked)
              if (checked) {
                form.setValue('required', false, { shouldDirty: true })
              }
            }}
          />
          <div className="flex flex-col gap-0.5">
            <FormLabel htmlFor="node-show-latest">Show latest value</FormLabel>
            <span className="text-xs text-muted-foreground">
              Fills in the participant&apos;s most recent recorded value; not
              answered in the note.
            </span>
          </div>
        </div>
      )}
    />
  )
  const calculationFields = !showLatest &&
    (methods.length > 1 || cutoffs.length > 1) && (
      <div className="flex flex-col gap-3 border-t pt-4">
        <span className="font-medium">Calculation</span>
        {methods.length > 1 && (
          <FormField
            control={form.control}
            name="derivedRuleId"
            render={({ field }) => (
              <div className="flex flex-col gap-1.5">
                <FormLabel htmlFor="node-derived-method">Method</FormLabel>
                <select
                  id="node-derived-method"
                  value={field.value}
                  onChange={(event) => field.onChange(event.target.value)}
                  className={SELECT_CLASS}
                >
                  {methods.map((rule) => (
                    <option key={rule.id} value={rule.id}>
                      {rule.method?.display ?? rule.id}
                    </option>
                  ))}
                </select>
              </div>
            )}
          />
        )}
        {cutoffs.length > 1 && (
          <FormField
            control={form.control}
            name="derivedBandsId"
            render={({ field }) => (
              <div className="flex flex-col gap-1.5">
                <FormLabel htmlFor="node-derived-cutoffs">Cutoffs</FormLabel>
                <select
                  id="node-derived-cutoffs"
                  value={field.value}
                  onChange={(event) => field.onChange(event.target.value)}
                  className={SELECT_CLASS}
                >
                  {cutoffs.map((set) => (
                    <option key={set.id} value={set.id}>
                      {set.display}
                    </option>
                  ))}
                </select>
              </div>
            )}
          />
        )}
      </div>
    )
  const source =
    node.attrs.system && node.attrs.code
      ? codeSource(node.attrs.system, node.attrs.code)
      : null

  return (
    <Form onSubmit={save} className="flex min-h-0 flex-1 flex-col text-sm">
      {/* The padding sits on the scroller, so a focused input's ring is never
       * cut at its edge. */}
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4">
        {isPanel ? (
          <>
            {requiredField}
            {showLatestField}
          </>
        ) : isSection ? (
          <FormField
            control={form.control}
            name="label"
            render={({ field: { onChange, ...field } }) => (
              <div className="flex flex-col gap-1.5">
                <FormLabel>Section label</FormLabel>
                <Input
                  {...field}
                  onChange={(event) => onChange(event.target.value)}
                  placeholder="e.g. Falls follow-up"
                  className="h-10"
                />
                <span className="text-xs text-muted-foreground">
                  Shown in the template only.
                </span>
              </div>
            )}
          />
        ) : (
          <>
            <div className="flex min-w-0 flex-col gap-1">
              <span className="text-base font-semibold break-words">
                {title}
              </span>
              {node.attrs.system && (
                <span
                  className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground"
                  title={`${node.attrs.system} · ${node.attrs.code}`}
                >
                  <IconLink className="size-3 shrink-0" />
                  {source?.external ? (
                    <a
                      href={source.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="min-w-0 truncate underline-offset-2 hover:underline"
                    >
                      {codeSystemName(node.attrs.system)}
                    </a>
                  ) : source ? (
                    <Link
                      to={source.href}
                      className="min-w-0 truncate underline-offset-2 hover:underline"
                    >
                      {codeSystemName(node.attrs.system)}
                    </Link>
                  ) : (
                    <span className="min-w-0 truncate">
                      {codeSystemName(node.attrs.system)}
                    </span>
                  )}
                </span>
              )}
            </div>
            <div className="flex flex-col gap-3 border-t pt-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field: { onChange, ...field } }) => (
                  <div className="flex flex-col gap-1.5">
                    <FormLabel
                      className="font-medium"
                      htmlFor={editingName ? 'node-name' : undefined}
                    >
                      Name{' '}
                      <span className="font-normal text-muted-foreground">
                        (for conditions)
                      </span>
                    </FormLabel>
                    {editingName ? (
                      <Input
                        {...field}
                        id="node-name"
                        onChange={(event) => onChange(event.target.value)}
                        placeholder="fh_cancer"
                        className="h-10"
                      />
                    ) : (
                      <div className="flex items-center gap-1">
                        <code className="min-w-0 truncate text-sm">
                          #{name.trim() || generated}
                        </code>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-6"
                          aria-label="Edit name"
                          onClick={() => setEditingName(true)}
                        >
                          <IconPencil className="size-3.5" />
                        </Button>
                      </div>
                    )}
                    {problem && (
                      <span role="alert" className="text-xs text-destructive">
                        {problem}
                      </span>
                    )}
                  </div>
                )}
              />
              {requiredField}
              {showLatestField}
            </div>
            {calculationFields}
          </>
        )}
        <div className="flex flex-col gap-3 border-t pt-4 first:border-t-0 first:pt-0">
          <Tabs
            value={tab}
            onValueChange={(next) => {
              if (next === 'advanced') {
                const built = builderToCondition(builder, sources)
                setText(built ? printCondition(built) : '')
              }
              setTab(next as 'builder' | 'advanced')
            }}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium">Logic</span>
              <TabsList className="h-7 p-0.5">
                <TabsTrigger value="builder" className="h-6 px-2 text-xs">
                  Builder
                </TabsTrigger>
                <TabsTrigger value="advanced" className="h-6 px-2 text-xs">
                  Advanced
                </TabsTrigger>
              </TabsList>
            </div>
            <TabsContent value="builder" className="mt-3">
              <ConditionBuilder
                value={builder}
                onChange={setBuilder}
                sources={sources}
              />
            </TabsContent>
            <TabsContent value="advanced" className="mt-3 flex flex-col gap-1">
              <Textarea
                aria-label="Condition"
                value={text}
                onChange={(next) => {
                  setText(next)
                  const result = next.trim() ? parseCondition(next) : null
                  // The builder follows the text whenever it can show it.
                  const asBuilder = result?.ok
                    ? conditionToBuilder(
                        result.condition,
                        sources,
                        pickedAnswers(builder)
                      )
                    : result === null
                      ? EMPTY_BUILDER
                      : null
                  if (asBuilder) {
                    setBuilder(asBuilder)
                  }
                }}
                placeholder="age >= 75 and #fh_cancer = yes"
                className="min-h-[140px] font-mono"
              />
              {parsed && !parsed.ok && (
                <span role="alert" className="text-xs text-destructive">
                  Column {parsed.error.column}: {parsed.error.message}
                </span>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
      <div className="flex items-center gap-2 border-t bg-background px-4 py-3">
        {removable && (
          <Button
            type="button"
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={() => {
              if (node.type.name === CONDITIONAL_SPAN_NODE) {
                editor.commands.unwrapConditionalSpan(pos)
              } else {
                editor.commands.unwrapConditionalSection(pos)
              }
              onDone()
            }}
          >
            {node.type.name === CONDITIONAL_SPAN_NODE
              ? 'Remove'
              : 'Remove section'}
          </Button>
        )}
        <span className="flex-1" />
        <Button type="submit">Save</Button>
      </div>
    </Form>
  )
}
